import React from 'react';
import { Stack, MenuItem, TextField, ListItemText, ListItemIcon } from '@mui/material';
import { GpsFixed as GpsFixedIcon, NearMe as NearMeIcon, RotateLeft as RotateLeftIcon, RotateRight as RotateRightIcon, Rotate90DegreesCcw as Rotate90DegreesCcwIcon, Rotate90DegreesCw as Rotate90DegreesCwIcon, } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { defineMessages, useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import API, { Types, useAuth, useCsrfToken } from '@wheeebo/polaris-api';
import { useSnackbar } from '@wheeebo/polaris-snackbar';
import { useErrorHandler } from '../errorHandler';
import { isAsv, vehicleActionTerms } from '../vehicle/vehicleTerms';
import MoveGlobalForm from './MoveGlobalForm';
import MoveRelativeForm from './MoveRelativeForm';
import RotateVehicleGlobalForm from './RotateVehicleGlobalForm';
import RotateVehicleRelativeForm from './RotateVehicleRelativeForm';
import RotateGimbalGlobalForm from './RotateGimbalGlobalForm';
import RotateGimbalRelativeForm from './RotateGimbalRelativeForm';
const msgs = defineMessages({
    successCommandSent: {
        id: 'pages.member.mission.FlightPlanCard.success.commandSent',
        defaultMessage: 'Command sent',
    },
});
function VehicleCommandForm({ vehicle }) {
    const intl = useIntl();
    const { account } = useAuth();
    const theme = useTheme();
    const { csrfToken } = useCsrfToken();
    const { showSuccess } = useSnackbar();
    const { showAndEmitJsErrorEvent } = useErrorHandler();
    const [commandType, setCommandType] = React.useState('');
    const { mutate: sendCommand } = useMutation((options) => {
        if (!account) {
            return Promise.reject('account is empty');
        }
        return API.Vehicles.command(csrfToken, vehicle.id, account?.id, options.command, options.props);
    }, {
        onSuccess: () => {
            showSuccess(intl.formatMessage(msgs.successCommandSent));
        },
        onError: showAndEmitJsErrorEvent,
    });
    const asv = isAsv(vehicle.vehicleType);
    const terms = vehicleActionTerms(vehicle.vehicleType);
    const handleSubmit = (command, props) => sendCommand({ command, props });
    const handleHoldPosition = () => {
        // Station keeping — marine "hover". HOLD_POSITION takes no params;
        // the on-vehicle autopilot anchors at the current position.
        sendCommand({ command: Types.VehicleCommandHoldPosition, props: {} });
    };
    return (React.createElement(Stack, { gap: 1, sx: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            p: 1,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: theme.palette.divider,
        } },
        React.createElement(TextField, { select: true, label: "Command", value: commandType, sx: {
                width: '90%',
                '& .MuiListItemIcon-root': {
                    minWidth: 0,
                    marginRight: 1,
                },
            }, inputProps: {
                sx: { display: 'flex', alignItems: 'center' },
            }, onChange: (e) => {
                const v = e.target.value;
                if (v === Types.VehicleCommandHoldPosition) {
                    handleHoldPosition();
                    return;
                }
                setCommandType(v);
            }, helperText: "Choose a command to execute." },
            asv ? (React.createElement(MenuItem, { value: Types.VehicleCommandHoldPosition },
                React.createElement(ListItemIcon, null,
                    React.createElement(GpsFixedIcon, { fontSize: "small" })),
                React.createElement(ListItemText, { primary: terms.holdPosition, secondary: terms.holdPositionHelp }))) : null,
            React.createElement(MenuItem, { value: Types.VehicleCommandMoveVehicleGlobal },
                React.createElement(ListItemIcon, null,
                    React.createElement(GpsFixedIcon, { fontSize: "small" })),
                React.createElement(ListItemText, { primary: "Move to a geo location" })),
            React.createElement(MenuItem, { value: Types.VehicleCommandMoveVehicleRelative },
                React.createElement(ListItemIcon, null,
                    React.createElement(NearMeIcon, { fontSize: "small" })),
                React.createElement(ListItemText, { primary: "Move relatively" })),
            React.createElement(MenuItem, { value: Types.VehicleCommandRotateVehicleGlobal },
                React.createElement(ListItemIcon, null,
                    React.createElement(RotateLeftIcon, { fontSize: "small" })),
                React.createElement(ListItemText, { primary: "Rotate vehicle globally" })),
            React.createElement(MenuItem, { value: Types.VehicleCommandRotateVehicleRelative },
                React.createElement(ListItemIcon, null,
                    React.createElement(RotateRightIcon, { fontSize: "small" })),
                React.createElement(ListItemText, { primary: "Rotate vehicle relatively" })),
            asv ? null : (React.createElement(MenuItem, { value: Types.VehicleCommandRotateGimbalGlobal },
                React.createElement(ListItemIcon, null,
                    React.createElement(Rotate90DegreesCcwIcon, { fontSize: "small" })),
                React.createElement(ListItemText, { primary: "Rotate gimbal globally" }))),
            asv ? null : (React.createElement(MenuItem, { value: Types.VehicleCommandRotateGimbalRelative },
                React.createElement(ListItemIcon, null,
                    React.createElement(Rotate90DegreesCwIcon, { fontSize: "small" })),
                React.createElement(ListItemText, { primary: "Rotate gimbal relatively" })))),
        commandType === Types.VehicleCommandMoveVehicleGlobal ? (React.createElement(MoveGlobalForm, { onSubmit: handleSubmit })) : commandType === Types.VehicleCommandMoveVehicleRelative ? (React.createElement(MoveRelativeForm, { onSubmit: handleSubmit })) : commandType === Types.VehicleCommandRotateVehicleGlobal ? (React.createElement(RotateVehicleGlobalForm, { onSubmit: handleSubmit })) : commandType === Types.VehicleCommandRotateVehicleRelative ? (React.createElement(RotateVehicleRelativeForm, { onSubmit: handleSubmit })) : commandType === Types.VehicleCommandRotateGimbalGlobal ? (React.createElement(RotateGimbalGlobalForm, { onSubmit: handleSubmit })) : commandType === Types.VehicleCommandRotateGimbalRelative ? (React.createElement(RotateGimbalRelativeForm, { onSubmit: handleSubmit })) : null));
}
export default React.memo(VehicleCommandForm);
